export type AgeBadgeStyle = {
  bg: string;
  fg: string;
};

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const lerpColor = (a: [number, number, number], b: [number, number, number], t: number) => {
  const tt = clamp(t, 0, 1);
  const r = Math.round(lerp(a[0], b[0], tt));
  const g = Math.round(lerp(a[1], b[1], tt));
  const b2 = Math.round(lerp(a[2], b[2], tt));
  return `rgb(${r}, ${g}, ${b2})`;
};

// Maps "days open/overdue" to a traffic-light gradient:
// deep green -> light green -> yellow -> light red -> deep red (>= 30 days).
export function getAgeBadgeStyle(days: number): AgeBadgeStyle {
  const d = clamp(Math.floor(days), 0, 3650);

  // stops: [days, fg, bg]
  const stops: Array<[number, [number, number, number], [number, number, number]]> = [
    [0, [16, 124, 16], [243, 249, 241]],     // deep green
    [7, [55, 146, 55], [236, 246, 236]],     // light green
    [14, [163, 109, 0], [255, 244, 230]],    // yellow/orange
    [21, [209, 52, 56], [254, 242, 242]],    // light red
    [30, [160, 32, 48], [255, 235, 235]],    // deep red
  ];

  const idx = stops.findIndex(([day]) => d <= day);
  if (idx <= 0) {
    const [, fg, bg] = stops[0];
    return { fg: `rgb(${fg[0]}, ${fg[1]}, ${fg[2]})`, bg: `rgb(${bg[0]}, ${bg[1]}, ${bg[2]})` };
  }
  if (idx === -1) {
    const [, fg, bg] = stops[stops.length - 1];
    return { fg: `rgb(${fg[0]}, ${fg[1]}, ${fg[2]})`, bg: `rgb(${bg[0]}, ${bg[1]}, ${bg[2]})` };
  }

  const [d0, fg0, bg0] = stops[idx - 1];
  const [d1, fg1, bg1] = stops[idx];
  const t = (d - d0) / Math.max(1, d1 - d0);

  return {
    fg: lerpColor(fg0, fg1, t),
    bg: lerpColor(bg0, bg1, t),
  };
}

export function daysSince(dateValue?: string | number | Date | null): number | null {
  if (!dateValue) return null;
  const date = new Date(dateValue as any);
  if (Number.isNaN(date.getTime())) return null;
  const diff = Date.now() - date.getTime();
  return diff >= 0 ? Math.floor(diff / (1000 * 60 * 60 * 24)) : 0;
}

